import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

export interface PublicSettings {
  certifiedNetworkNotificationEmail: string;
  generalContactEmail: string;
  generalContactPhone: string;
  organizationName: string;
  // Dipendenti dello studio: true se la funzione non è spenta (scheda «Sono un dipendente» del login)
  dipendentiLoginVisibile?: boolean;
}

export interface VerifyVATResponse {
  success: boolean;
  associate?: {
    id: string;
    codice: string;
    nominativo: string;
    partitaIVA: string;
    email: string;
    telefono: string;
    indirizzo: string;
  };
  hasExistingApplication?: boolean;
  existingApplicationId?: string;
  message?: string;
  reason?: string;
}

export interface CertifiedNetworkApplication {
  _id?: string;
  associateId: string;
  partitaIVA: string;
  
  studioData: {
    ragioneSociale: string;
    partitaIVA: string;
    codiceFiscale: string;
    sedeLegale: {
      indirizzo: string;
      citta: string;
      provincia: string;
      cap: string;
    };
    sedeOperativa: {
      indirizzo: string;
      citta: string;
      provincia: string;
      cap: string;
    };
    telefono: string;
    email: string;
    sitoWeb?: string;
    numeroDipendenti: number;
    totaleAddetti: number;
  };
  
  legalRepresentative: {
    nome: string;
    cognome: string;
    codiceFiscale: string;
    ruolo: string;
    numeroTesseraUNASCA: string;
  };
  
  sermetra: {
    isConsorziato: boolean;
    numeroConsorziato?: string;
  };
  
  dpo: {
    nomeCompleto: string;
    isNominato: boolean;
    descrizioneProtezione: string;
  };
  
  gdpr: {
    hasMisure: boolean;
    hasDPIA: boolean;
  };
  
  insurance: {
    hasInsurance: boolean;
    isSermetra: boolean;
    compagnia?: string;
    numeroPolizza?: string;
    massimale?: string;
  };
  
  cybersecurity: {
    hasMisure: boolean;
    descrizioneSoluzioni: string;
  };
  
  preAuthorizations: Array<{
    materia: string;
    selected: boolean;
  }>;
  
  training: {
    hasFrequentato: boolean;
    corsiFrequentati: string;
    documenti: Array<{
      filename: string;
      originalName: string;
      path: string;
      uploadedAt: Date;
    }>;
  };
  
  ebas: {
    versaDiritti: boolean;
  };
  
  services: string[];
  
  collaborationObligations: {
    accepted: boolean;
    acceptedAt?: Date;
  };
  
  declaration: {
    accepted: boolean;
    signerName: string;
    signedAt?: Date;
  };
  
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  submittedAt?: Date;
}

/**
 * Verifica Partita IVA e idoneità iscrizione
 */
export const verifyVAT = async (partitaIVA: string): Promise<VerifyVATResponse> => {
  const response = await axios.post(`${API_BASE_URL}/certified-network/verify-vat`, {
    partitaIVA
  });
  return response.data;
};

/**
 * Crea nuova domanda di iscrizione
 */
export const createApplication = async (
  associateId: string,
  partitaIVA: string
): Promise<{ success: boolean; applicationId: string; message: string }> => {
  const response = await axios.post(`${API_BASE_URL}/certified-network/applications`, {
    associateId,
    partitaIVA
  });
  return response.data;
};

/**
 * Recupera domanda esistente
 */
export const getApplication = async (
  applicationId: string
): Promise<{ success: boolean; application: CertifiedNetworkApplication }> => {
  const response = await axios.get(`${API_BASE_URL}/certified-network/applications/${applicationId}`);
  return response.data;
};

/**
 * Aggiorna domanda (salvataggio parziale)
 */
export const updateApplication = async (
  applicationId: string,
  data: Partial<CertifiedNetworkApplication>,
  editToken?: string
): Promise<{ success: boolean; application: CertifiedNetworkApplication; message: string }> => {
  const response = await axios.put(
    `${API_BASE_URL}/certified-network/applications/${applicationId}`,
    editToken ? { ...data, editToken } : data
  );
  return response.data;
};

/**
 * Invia domanda per approvazione
 */
export const submitApplication = async (
  applicationId: string,
  editToken?: string
): Promise<{ success: boolean; application: CertifiedNetworkApplication; message: string }> => {
  const response = await axios.post(
    `${API_BASE_URL}/certified-network/applications/${applicationId}/submit`,
    editToken ? { editToken } : {}
  );
  return response.data;
};

/**
 * Salva e invia link temporaneo via email per riprendere dopo
 */
export const saveAndSendLink = async (
  applicationId: string
): Promise<{ success: boolean; message: string; data: { editLink: string; expiresAt: Date } }> => {
  const response = await axios.post(
    `${API_BASE_URL}/certified-network/applications/${applicationId}/save-and-send-link`
  );
  return response.data;
};

/**
 * Recupera domanda tramite token temporaneo
 */
export const getApplicationByToken = async (
  token: string
): Promise<{ success: boolean; application: CertifiedNetworkApplication }> => {
  const response = await axios.get(
    `${API_BASE_URL}/certified-network/resume/${token}`
  );
  return response.data;
};

/**
 * Upload documenti formazione
 */
export const uploadDocuments = async (
  applicationId: string,
  files: File[]
): Promise<{ success: boolean; documents: any[]; message: string }> => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('documents', file);
  });

  const response = await axios.post(
    `${API_BASE_URL}/certified-network/applications/${applicationId}/upload-documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

/**
 * Recupera impostazioni pubbliche
 */
export const getPublicSettings = async (): Promise<{ success: boolean; data: PublicSettings }> => {
  const response = await axios.get(`${API_BASE_URL}/public/settings`);
  return response.data;
};

/**
 * Materie disponibili per pre-autorizzazione
 */
export const PRE_AUTHORIZATION_SUBJECTS = [
  'Autentica di firma',
  'Immatricolazioni e passaggi di proprietà',
  'Consulenza per le pratiche agricole e operatrici',
  'Trasporto merci',
  'Nazionalizzazioni',
  'Consulenze tecniche',
  'Trasporti eccezionali',
  'Servizi nautici',
  'Trasporto rifiuti',
  'Rinnovo patenti'
];

/**
 * Servizi offerti disponibili
 */
export const AVAILABLE_SERVICES = [
  'Macchine agricole e operatrici',
  'Trasporto merci',
  'Trasporto Rifiuti',
  'Rinnovo patenti',
  'Pratiche nautiche',
  'Prenotazione revisioni',
  'Riscossione bolli',
  'Autorizzazioni trasporti eccezionali',
  'Corsi di formazione autotrasporto'
];

/**
 * Province italiane
 */
export const ITALIAN_PROVINCES = [
  'AG', 'AL', 'AN', 'AO', 'AP', 'AQ', 'AR', 'AT', 'AV', 'BA', 'BG', 'BI', 'BL', 'BN', 'BO', 'BR', 'BS', 'BT', 'BZ',
  'CA', 'CB', 'CE', 'CH', 'CI', 'CL', 'CN', 'CO', 'CR', 'CS', 'CT', 'CZ',
  'EN', 'FC', 'FE', 'FG', 'FI', 'FM', 'FR', 'FU',
  'GE', 'GO', 'GR',
  'IM', 'IS',
  'KR',
  'LC', 'LE', 'LI', 'LO', 'LT', 'LU',
  'MB', 'MC', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT',
  'NA', 'NO', 'NU',
  'OG', 'OR', 'OT',
  'PA', 'PC', 'PD', 'PE', 'PG', 'PI', 'PN', 'PO', 'PR', 'PT', 'PU', 'PV', 'PZ',
  'RA', 'RC', 'RE', 'RG', 'RI', 'RM', 'RN', 'RO',
  'SA', 'SI', 'SO', 'SP', 'SR', 'SS', 'SU', 'SV',
  'TA', 'TE', 'TN', 'TO', 'TP', 'TR', 'TS', 'TV',
  'UD',
  'VA', 'VB', 'VC', 'VE', 'VI', 'VR', 'VS', 'VT', 'VV'
];

